import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import type { FanResponseDto } from '@api'
import { useUnit } from 'effector-react'
import { usePersistentState } from 'react-persistent-state'
import { useSearchParams } from 'react-router-dom'

import { deleteChat, setFanId } from '@/models/chattingUI/chats/chats.ts'
import { removeUserEditorModel } from '@/models/chattingUI/messageEditor/messageEditor'
import { setMessageIdToFetch } from '@/models/chattingUI/messages/messages.ts'
import { $creatorId } from '@/models/creators.ts'
import type { ChattingTab } from '@/modules/chatting/types.ts'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils.ts'
import { noop } from '@/utils/misc.ts'

type TabsContextValue = ReturnType<typeof useComputeTabs>
export const TabsContext = createContext<TabsContextValue | null>(null)
const MAX_TABS_COUNT = 20

export const newTab: ChattingTab = {
  fanId: -1,
  isActive: true,
  title: 'New tab',
}

export type TabInitialData = Pick<
  ChattingTab,
  'avatar' | 'fanNumber' | 'fanId' | 'title'
>

const getTabConfig = (fan?: TabInitialData): Omit<ChattingTab, 'isActive'> => {
  const title = fan ? unescapeHtmlText(fan.title) : newTab.title
  const fanId = fan ? fan.fanId : newTab.fanId

  return {
    title,
    fanId,
    avatar: fan?.avatar,
    fanNumber: fan?.fanNumber,
  }
}

export const useChattingTabs = () => {
  const context = useContext(TabsContext)

  if (!context) {
    throw new Error('useChats must be used within ChatsListContextProvider')
  }

  return context
}

export let closeTabCallback: (chatId: number) => void = noop
// hack to work with effector, temporary
deleteChat.watch((chatId) => {
  closeTabCallback(chatId)
})

export const useComputeTabs = () => {
  const creatorId = useUnit($creatorId)
  const [searchParams, setSearchParams] = useSearchParams()

  const [tabs, setTabs] = usePersistentState<ChattingTab[]>([newTab], {
    storageKey: `${creatorId}-chatting-tabs`,
  })

  useEffect(() => {
    // passed from desktop
    const RESET_TABS = 'reset-tabs'

    if (searchParams.get(RESET_TABS) === 'true') {
      setTabs([newTab])
      setSearchParams((prev) => {
        prev.delete(RESET_TABS)

        return prev
      })
    }
  }, [searchParams, setTabs, setSearchParams])

  const setTabFan = useCallback(
    (fan?: TabInitialData) => {
      setTabs((prev) =>
        prev.map((tab) => {
          if (tab.isActive && tab.fanId !== fan?.fanId) {
            setMessageIdToFetch(null)

            return { ...tab, ...getTabConfig(fan) }
          }

          return tab
        }),
      )
    },
    [setTabs],
  )

  const setTabFanName = useCallback(
    (fanId: number, title: string) => {
      setTabs((prev) =>
        prev.map((tab) => {
          if (tab.fanId === fanId) {
            return { ...tab, title }
          }

          return tab
        }),
      )
    },
    [setTabs],
  )

  const changeActiveTab = useCallback(
    (tabIndex: number) => {
      setTabs((tabs) =>
        tabs.map((tab, index) => {
          if (index === tabIndex) {
            return {
              ...tab,
              isActive: true,
            }
          } else {
            return {
              ...tab,
              isActive: false,
            }
          }
        }),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const isTabsLimitReached = tabs.length >= MAX_TABS_COUNT

  const openNewTab = useCallback(
    (fan?: TabInitialData) => {
      if (isTabsLimitReached) return

      setTabs((prev) =>
        prev
          .map((tab) => ({ ...tab, isActive: false }))
          .concat({ ...newTab, ...getTabConfig(fan), isActive: true }),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTabsLimitReached],
  )

  const closeTab = useCallback(
    (tabIndex: number) => {
      setTabs((tabs) => {
        if (tabs.length === 1 && tabIndex === 0) {
          return [newTab]
        }

        const tabsClone = [...tabs]
        const currentTab = tabs[tabIndex]

        if (currentTab?.isActive) {
          const prevIndex = tabIndex - 1
          const nextIndex = tabIndex + 1

          if (prevIndex >= 0) {
            tabsClone[prevIndex] = { ...tabsClone[prevIndex], isActive: true }
          } else if (nextIndex < tabsClone.length) {
            tabsClone[nextIndex] = { ...tabsClone[nextIndex], isActive: true }
          }
        }
        tabsClone.splice(tabIndex, 1)
        const isAllFanTabsClosed = !tabsClone.some(
          (tab) => tab.fanId === currentTab?.fanId,
        )

        if (isAllFanTabsClosed) {
          removeUserEditorModel(currentTab?.fanId)
        }

        return tabsClone
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const closeChatsWithUserId = useCallback(
    (fanId: number) => {
      setTabs((old) => {
        const newVal = old.filter((o) => (o.fanId ? o.fanId !== fanId : true))

        if (!newVal.length) {
          return [newTab]
        }

        if (!newVal.find((tab) => tab.isActive)) {
          newVal[0].isActive = true
        }

        return newVal
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const moveTabs = useCallback(
    (origin: ChattingTab, target: ChattingTab) => {
      const newTabs = [...tabs]
      const originIndex = newTabs.indexOf(origin)
      const targetIndex = newTabs.indexOf(target)

      newTabs[originIndex] = target
      newTabs[targetIndex] = origin

      setTabs(newTabs)
    },
    [tabs, setTabs],
  )

  const activeTabIndex = tabs.findIndex((tab) => tab.isActive)
  const activeTabsFanId = tabs[activeTabIndex].fanId

  useEffect(() => {
    setFanId(activeTabsFanId)
  }, [activeTabsFanId])

  useEffect(() => {
    closeTabCallback = closeChatsWithUserId
  }, [closeChatsWithUserId])

  const values = useMemo(
    () => ({
      tabs,
      activeTabIndex,
      activeTabsFanId,
      setTabFan,
      openNewTab,
      closeTab,
      changeActiveTab,
      closeChatsWithUserId,
      isTabsLimitReached,
      setTabFanName,
      moveTabs,
    }),
    [
      tabs,
      activeTabIndex,
      activeTabsFanId,
      setTabFan,
      openNewTab,
      closeTab,
      changeActiveTab,
      closeChatsWithUserId,
      isTabsLimitReached,
      setTabFanName,
      moveTabs,
    ],
  )

  return values
}
